<template>
    <transition :name="transition">
        <div class="step" :class="step.class" v-show="isCurrentStep">
            <div class="step-content">
                <template v-if="step.id === 'summary'">

                    <Summary />
                    <AddToCart />
                    <SaveDesign />
                </template>

                <template v-else>
                    <h2 class="title" v-if="step.title">{{ step.title }}</h2>
                    <p class="description" v-if="step.description">{{ step.description }}</p>

                    <div class="step-options">
                        <Option v-for="(option, i) in stepOptions" :option="option" :key="i" />
                    </div>

                    <div class="login-reminder" v-if="!magentoData.customerId && !iteration && loginLink">
                        <p>Note: If you wish to save your design, please <a :href="loginLink">log in or create an account</a>.</p>
                        <p>This visualizer is best viewed in a modern browser, such as Chrome.</p>
                    </div>

                </template>
            </div>
        </div>
    </transition>
</template>

<script>
import { mapState } from "vuex";
import Option from "@/components/Option";
import Summary from "@/components/Summary";
import AddToCart from "@/components/AddToCart";
import SaveDesign from "@/components/SaveDesign";
import StepValidator from "@/components/StepValidator";

export default {
	name: "Step",
	extends: StepValidator,
	props: ["step", "iteration"],
	components: { Option, Summary, AddToCart, SaveDesign },
	computed: {
		transition() {
			return this.$store.getters["configurator/stepDirection"] > 0 ? "slide-left" : "slide-right";
		},
		stepOptions() {
			return this.step.options ? this.config.options.filter(opt => this.step.options.includes(opt.id)) : [];
		},
		allStepIds() {
			return this.config.steps.map(step => step.id);
		},
		previousStepIds() {
			let stepIndex = this.allStepIds.indexOf(this.currentStep);
			return this.allStepIds.slice(0, stepIndex);
		},
		nextStepIds() {
			let stepIndex = this.allStepIds.indexOf(this.currentStep) + 1;
			return this.allStepIds.slice(stepIndex);
		},
		previousStepId() {
			return this.previousStepIds.length > 0 ? this.previousStepIds[this.previousStepIds.length - 1] : this.currentStep;
		},
		nextStepId() {
			return this.nextStepIds.length > 0 ? this.nextStepIds[0] : this.currentStep;
		},
		isCurrentStep() {
			return this.step.id === this.currentStep;
        },
        loginLink() {
            return (this.magentoData.baseUrl) ? this.magentoData.baseUrl + 'Login-Show' : null;
        },
		...mapState({
			config: state => state.configurator.config,
			currentStep: state => state.configurator.currentStep,
			magentoData: state => state.configurator.magentoData
		})
	},
	methods: {
		moveToPreviousStep() {
			this.$store.dispatch("configurator/setCurrentStep", this.previousStepId);
		},
		moveToNextStep() {
			if (this.isValidCurrentStep()) {
				// if no validation errors found, update store with next step id.
				this.$store.dispatch("configurator/setCurrentStep", this.nextStepId);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
$animation-speed: 500ms;
.step {
    transition: transform $animation-speed;
    position: relative;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    padding: 16px;
    margin-bottom: 80px; // Height of the StepNavigation

    .step-content {
        .title {
            all: unset;
            display: block;
            font-weight: 300;
            margin: 8px 0 24px;
            text-align: center;
            font-size: 21px;
        }

        .description {
            text-align: center;
            margin-top: -8px;
            margin-bottom: 24px;
        }

        .login-reminder {
            text-align: center;
            color: $color-red;
            a {
                color: $color-red;
                font-weight: bold;
                white-space: nowrap;
            }
        }
    }

    // Slide transition
    &.slide-left-enter,
    &.slide-right-leave-to {
        transform: translate(100%, 0);
    }
    &.slide-right-enter,
    &.slide-left-leave-to {
        transform: translate(-100%, 0);
    }
    &.slide-right-enter,
    &.slide-right-leave-to,
    &.slide-left-enter,
    &.slide-left-leave-to {
        position: absolute;
        height: 100%;
    }

    &.in-row {
        .step-options {
            display: flex;
            justify-content: center;
            .option {
                display: flex;
                margin: 8px;
            }
        }

        &.plus {
            .step-options .option {
                margin: 0;
                &:last-of-type {
                    &:before {
                        content: "+";
                        display: block;
                        font-size: 20px;
                        padding: 0 8px;
                        line-height: 50px;
                    }
                }
            }
        }
    }

    @media only screen and (min-width: $viewport-desktop) {
        position: absolute;
        display: flex;
        flex-direction: column;
        height: 100%;
        margin-bottom: 0;
        .step-content {
            margin: auto 0; // Center content vertically
            .title {
                font-size: 30px;
                margin: 32px 0;
            }
        }
    }
}
</style>
